import React, { useState, useEffect, useRef } from "react";
import Card from "../components/Card.js";
import DropZone from "../components/DropZone.js";
import { createInitialGameState, playToFoundation, moveOnTableau, drawCards, recycleDrawPile, calculateScores } from "../game/gameEngine.js";
import { makeAIMove, getAIDelay } from "../game/aiPlayer.js";
import { DIFFICULTIES } from "../utils/constants.js";
import AIProgressBar from "../components/AIProgressBar.js";
import { saveGameResult } from "../utils/statsUtils.js";
import { sounds } from "../utils/sounds.js";

function hasAnyValidMove(state, player) {
  const p = state[player];
  const pounceTop = p.pouncePile[p.pouncePile.length - 1];
  const discardTop = p.drawDiscard[p.drawDiscard.length - 1];
  if (p.drawPile.length > 0) return true;
  if (pounceTop) {
    if (playToFoundation(state, player, 0, 'pouncePile')) return true;
    for (let to = 0; to < p.tableau.length; to++) {
      if (moveOnTableau(state, player, 0, to, 'pouncePile', false)) return true;
    }
  }
  if (discardTop) {
    if (playToFoundation(state, player, 0, 'drawDiscard')) return true;
    for (let to = 0; to < p.tableau.length; to++) {
      if (moveOnTableau(state, player, 0, to, 'drawDiscard', false)) return true;
    }
  }
  for (let i = 0; i < p.tableau.length; i++) {
    if (playToFoundation(state, player, i, 'tableau')) return true;
    for (let j = 0; j < p.tableau.length; j++) {
      if (i === j) continue;
      if (moveOnTableau(state, player, i, j, 'tableau', false)) return true;
      if (moveOnTableau(state, player, i, j, 'tableau', true)) return true;
    }
  }
  return false;
}

const TOOLBAR_H = 56;
const CARD_OVERLAP = 0.28;
const MOBILE_CARD_OVERLAP = 0.22; // how much of card height shows when stacked

export default function GameVsAI({ onBack, difficulty = "medium", playerName = "Player", userId }) {
  const [gameState, setGameState] = useState(() => createInitialGameState(playerName, difficulty));
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedIsStack, setSelectedIsStack] = useState(false);
  const [message, setMessage] = useState("Your turn — play fast!");
  const [cardSize, setCardSize] = useState(1.1);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [shakingCard, setShakingCard] = useState(null);
  const [aiFlash, setAiFlash] = useState(false);
  const [dragData, setDragData] = useState(null);
  const gameStateRef = useRef(gameState);
  const touchDragRef = useRef(null);
  const touchDragElRef = useRef(null);
  const phaseRef = useRef("playing");
  const startTimeRef = useRef(Date.now());

  useEffect(() => { gameStateRef.current = gameState; phaseRef.current = gameState ? gameState.phase : "playing"; }, [gameState]);
  useEffect(() => { sounds.setEnabled(soundEnabled); }, [soundEnabled]);

  // Touch drag - document level for iOS Safari
  useEffect(() => {
    function onTouchMove(e) {
      if (!touchDragRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const el = touchDragElRef.current;
      if (el) {
        el.style.left = (touch.clientX - el.offsetWidth / 2) + 'px';
        el.style.top = (touch.clientY - el.offsetHeight / 2) + 'px';
      }
      // Highlight targets
      document.querySelectorAll('[data-droptarget]').forEach(t => {
        t.style.outline = '';
        t.style.backgroundColor = '';
      });
      if (el) el.style.pointerEvents = 'none';
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      if (el) el.style.pointerEvents = 'none';
      if (target) {
        const drop = target.closest('[data-droptarget]');
        if (drop) {
          drop.style.outline = '2px solid #c9a84c';
          drop.style.backgroundColor = 'rgba(201,168,76,0.15)';
        }
      }
    }

    function onTouchEnd(e) {
      if (!touchDragRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.changedTouches[0];
      document.querySelectorAll('[data-droptarget]').forEach(t => {
        t.style.outline = '';
        t.style.backgroundColor = '';
      });
      const el = touchDragElRef.current;
      if (el) { el.style.opacity = '0'; setTimeout(() => el && el.remove(), 150); touchDragElRef.current = null; }
      if (el) el.style.pointerEvents = 'none';
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      const drop = target ? target.closest('[data-droptarget]') : null;
      const drag = touchDragRef.current;
      touchDragRef.current = null;
      if (!drop || !drag) return;
      if (drop.dataset.droptarget === 'foundation') {
        setSelectedCard(drag.card);
        setSelectedSource(drag.source);
        setSelectedIndex(drag.index);
        setSelectedIsStack(drag.isStack || false);
        setTimeout(() => handleDropOnFoundationRef.current(), 0);
      } else if (drop.dataset.droptarget.startsWith('tableau-')) {
        const idx = parseInt(drop.dataset.droptarget.split('-')[1]);
        setSelectedCard(drag.card);
        setSelectedSource(drag.source);
        setSelectedIndex(drag.index);
        setSelectedIsStack(drag.isStack || false);
        setTimeout(() => handleDropOnTableauRef.current(idx), 0);
      }
    }

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const handleDropOnFoundationRef = useRef(null);
  const handleDropOnTableauRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    function scheduleAI() {
      if (cancelled) return;
      setTimeout(() => {
        if (cancelled) return;
        const current = gameStateRef.current;
        if (!current || phaseRef.current !== "playing") { scheduleAI(); return; }
        const newState = makeAIMove(current);
        if (newState) {
          const action = newState.lastAction;
          console.log("AI action:", action?.type);
          if (action && action.type === "foundation") {
            // ai sound removed
          }
          setGameState(newState);
          if (newState.phase === "pounce" && newState.winner === "ai") {
            sounds.play("pounce");
            setMessage("The Dealer POUNCED! Game over!");
            saveGameResult({ userId, won: false, cardsPlayed: 0, roundTimeSeconds: 0 });
            setTimeout(() => setGameState(calculateScores(newState)), 1500);
            return;
          }
        }
        scheduleAI();
      }, getAIDelay(difficulty));
    }
    scheduleAI();
    return () => { cancelled = true; };
  }, [difficulty, userId]);

  // AI sound placeholder - disabled for now

  useEffect(() => {
    if (!gameState || gameState.phase !== "playing") return;
    const p = gameState.player;
    const a = gameState.ai;
    const playerTrulyStuck = p.drawPile.length === 0 && p.drawDiscard.length === 0 && !hasAnyValidMove(gameState, "player");
    const aiTrulyStuck = a.drawPile.length === 0 && a.drawDiscard.length === 0 && !hasAnyValidMove(gameState, "ai");
    if (playerTrulyStuck && aiTrulyStuck) {
      setMessage("No moves available — round over!");
      setTimeout(() => setGameState(calculateScores(gameState)), 2000);
    }
  }, [gameState]);

  function startNewGame() {
    setGameState(createInitialGameState(playerName, difficulty));
    clearSelection();
    setMessage("Your turn — play fast!");
    startTimeRef.current = Date.now();
  }

  function handleRecycle() {
    const gs = gameStateRef.current;
    if (!gs || gs.phase !== "playing") return;
    if (gs.player.drawPile.length === 0) { setMessage("Nothing to recycle!"); return; }
    const newState = recycleDrawPile(gs, "player");
    if (newState) { setGameState(newState); sounds.play("recycle"); setMessage("Draw pile shuffled!"); }
  }

  function shakeFeedback(id) {
    sounds.play("invalid");
    setShakingCard(id);
    setTimeout(() => setShakingCard(null), 400);
  }


  function startTouchDrag(e, card, source, index, isStack) {
    if (!card || !card.faceUp) return;
    e.stopPropagation();
    // Store drop target rects at drag start time
    const rects = {};
    document.querySelectorAll("[data-droptarget]").forEach(el => {
      rects[el.dataset.droptarget] = el.getBoundingClientRect();
    });
    touchDragRef.current = { card, source, index, isStack, rects };
    setSelectedCard(card);
    setSelectedSource(source);
    setSelectedIndex(index);
    setSelectedIsStack(isStack || false);
    const touch = e.touches[0];
    const isRed = card.suit === "♥" || card.suit === "♦";
    const color = isRed ? "#cc0000" : "#111111";
    const w = Math.round(60 * cs);
    const h = Math.round(84 * cs);
    const el = document.createElement("div");
    el.style.cssText = `position:fixed;width:${w}px;height:${h}px;background:#faf7f0;border:2px solid #c9a84c;border-radius:6px;padding:5px;pointer-events:none;z-index:9999;box-shadow:0 12px 30px rgba(0,0,0,0.6);display:flex;flex-direction:column;justify-content:flex-start;opacity:0.95;left:${touch.clientX - w/2}px;top:${touch.clientY - h - 30}px;transition:opacity 0.15s;`;
    el.innerHTML = `<div style="color:${color};font-size:${Math.round(18*cs)}px;font-weight:900;line-height:1"><div>${card.value}</div><div style="font-size:${Math.round(14*cs)}px">${card.suit}</div></div><div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-40%);font-size:${Math.round(36*cs)}px;color:${color};opacity:0.2">${card.suit}</div>`;
    document.body.appendChild(el);
    touchDragElRef.current = el;
  }

  function handleDragStart(card, source, index, isStack) {
    setDragData({ card, source, index, isStack });
    setSelectedCard(card);
    setSelectedSource(source);
    setSelectedIndex(index);
    setSelectedIsStack(isStack || false);
  }

  function handleDragEnd() { setDragData(null); }

  function handleDropOnFoundation(touchDrag) {
    const gs = gameStateRef.current;
    const src = touchDrag || dragData || { card: selectedCard, source: selectedSource, index: selectedIndex };
    if (!src.card || !gs) return;
    const newState = playToFoundation(gs, "player", src.index, src.source);
    if (newState) {
      sounds.play("place");
      setGameState(newState);
      setMessage(`Played ${src.card.value}${src.card.suit} to foundation!`);
      clearSelection();
      if (newState.phase === "pounce" && newState.winner === "player") {
        sounds.play("pounce");
        setMessage("POUNCE! You emptied your pile!");
        const roundTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
        saveGameResult({ userId, won: true, cardsPlayed: newState.foundations.flat().filter(c => c.id.endsWith("-0")).length, roundTimeSeconds: roundTime });
        setTimeout(() => setGameState(calculateScores(newState)), 1500);
      }
    } else { shakeFeedback(src.card.id); setMessage("Cannot play there!"); }
    setDragData(null);
  }

  function handleDropOnTableau(toIndex, touchDrag) {
    const gs = gameStateRef.current;
    const src = touchDrag || dragData || { card: selectedCard, source: selectedSource, index: selectedIndex, isStack: selectedIsStack };
    if (!src.card || !gs) return;
    const newState = moveOnTableau(gs, "player", src.index, toIndex, src.source, src.isStack);
    if (newState) { sounds.play("place"); setGameState(newState); setMessage("Moved!"); clearSelection(); }
    else { shakeFeedback(src.card.id); setMessage("Cannot move there!"); }
    setDragData(null);
  }

  function handleCardClick(source, index, isStack) {
    const gs = gameStateRef.current;
    if (!gs || gs.phase !== "playing") return;
    const player = gs.player;

    if (source === "draw") {
      const newState = drawCards(gs, "player");
      if (newState) { sounds.play("draw"); setGameState(newState); setMessage("Drew cards"); }
      else setMessage("No cards to draw!");
      clearSelection();
      return;
    }

    let card = null;
    if (source === "pouncePile") card = player.pouncePile[player.pouncePile.length - 1];
    else if (source === "tableau") {
      const stack = player.tableau[index] || [];
      card = isStack ? stack[0] : stack[stack.length - 1];
    }
    else if (source === "drawDiscard") card = player.drawDiscard[player.drawDiscard.length - 1];
    if (!card) { setMessage("No card there!"); return; }

    if (selectedCard) {
      if (selectedCard.id === card.id) { clearSelection(); return; }
      if (source === "tableau") {
        const newState = moveOnTableau(gs, "player", selectedIndex, index, selectedSource, selectedIsStack);
        if (newState) { sounds.play("place"); setGameState(newState); setMessage("Moved!"); clearSelection(); return; }
        else { shakeFeedback(card.id); setMessage("Cannot move there!"); clearSelection(); return; }
      }
      if (source === "foundation") { handleDropOnFoundation(); return; }
    }

    setSelectedCard(card);
    setSelectedSource(source);
    setSelectedIndex(index);
    setSelectedIsStack(isStack || false);
    setMessage(`Selected ${card.value}${card.suit}${isStack ? " (stack)" : ""} — drop on foundation or tableau`);
  }

  function clearSelection() {
    setSelectedCard(null);
    setSelectedSource(null);
    setSelectedIndex(null);
    setSelectedIsStack(false);
  }

  const isMobile = window.innerWidth <= 768;
  const cs = isMobile ? 0.90 : cardSize;
  const cardW = Math.round(60 * cs);
  const cardH = Math.round(84 * cs);
  const overlapH = Math.round(cardH * CARD_OVERLAP);
  const diffInfo = DIFFICULTIES[difficulty];
  const canRecycle = gameState ? gameState.player.drawPile.length > 0 : false;

  // Keep refs updated
  useEffect(() => {
    handleDropOnFoundationRef.current = handleDropOnFoundation;
    handleDropOnTableauRef.current = handleDropOnTableau;
  });

  function renderTableauStack(stack, tableauIndex, isAI) {
    if (!stack || stack.length === 0) {
      return (
        <div
          onClick={isAI ? undefined : () => handleCardClick("tableau", tableauIndex, false)}
          style={{ width: cardW * (isAI ? 0.75 : 1) + "px", height: cardH * (isAI ? 0.75 : 1) + "px", borderRadius: "6px", border: "2px dashed rgba(201,168,76,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(201,168,76,0.2)", fontSize: "0.7rem", cursor: isAI ? "default" : "pointer" }}
        >
          empty
        </div>
      );
    }

    const size = isAI ? cs * 0.75 : cs;
    const cw = Math.round(60 * size);
    const ch = Math.round(84 * size);
    const oh = Math.round(ch * (isAI ? CARD_OVERLAP : (isMobile ? MOBILE_CARD_OVERLAP : CARD_OVERLAP)));
    const maxHeight = isMobile ? Math.round(ch * 1.6) : ch * 6;
    const totalHeight = Math.min(ch + (stack.length - 1) * oh, maxHeight);
    const actualOh = stack.length > 1 ? Math.min(oh, Math.round((totalHeight - ch) / (stack.length - 1))) : oh;

    return (
      <div style={{ position: "relative", width: cw + "px", height: totalHeight + "px", flexShrink: 0 }}>
        {stack.map((card, i) => {
          const isTop = i === stack.length - 1;
          const isBottom = i === 0;
          const isSelected = !isAI && selectedSource === "tableau" && selectedIndex === tableauIndex &&
            (selectedIsStack ? isBottom : isTop) && selectedCard && selectedCard.id === card.id;

          return (
            <div
              key={card.id}
              style={{ position: "absolute", top: i * actualOh + "px", left: 0, zIndex: i }}
            >
              <Card
                card={card}
                cardSize={size}
                selected={isSelected}
                shake={shakingCard === card.id}
                draggable={!isAI && (isTop || isBottom)}
                disabled={isAI}
                onClick={isAI ? undefined : () => handleCardClick("tableau", tableauIndex, isBottom && stack.length > 1)}
                onDragStart={isAI ? undefined : () => handleDragStart(card, "tableau", tableauIndex, isBottom && stack.length > 1)}
                onTouchStart={isAI ? undefined : (e) => startTouchDrag(e, card, "tableau", tableauIndex, isBottom && stack.length > 1)}
                onDragEnd={handleDragEnd}
              />
            </div>
          );
        })}
      </div>
    );
  }

  if (!gameState) return <div style={{ minHeight: "100vh", backgroundColor: "#0d1b0f", display: "flex", alignItems: "center", justifyContent: "center" }}><p style={{ color: "#c9a84c" }}>Dealing...</p></div>;

  return (
    <div
      style={{ height: "100vh", width: "100vw", backgroundColor: "#0d1b0f", backgroundImage: "radial-gradient(ellipse at 50% 50%, rgba(26,71,42,0.4) 0%, transparent 70%)", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "Lato, sans-serif", touchAction: "none" }}
      onTouchMove={(e) => {
        if (!touchDragRef.current) return;
        e.preventDefault();
        const touch = e.touches[0];
        const el = touchDragElRef.current;
        if (el) {
          el.style.left = (touch.clientX - el.offsetWidth/2) + "px";
          el.style.top = (touch.clientY - el.offsetHeight / 2) + "px";
        }
        document.querySelectorAll("[data-droptarget]").forEach(t => { t.style.outline = ""; t.style.backgroundColor = ""; });
        if (el) el.style.display = "none";
        const tgt = document.elementFromPoint(touch.clientX, touch.clientY);
        if (el) el.style.display = "flex";
        if (tgt) {
          const drop = tgt.closest("[data-droptarget]");
          if (drop) { drop.style.outline = "2px solid #c9a84c"; drop.style.backgroundColor = "rgba(201,168,76,0.15)"; }
        }
      }}
      onTouchEnd={(e) => {
        if (!touchDragRef.current) return;
        const touch = e.changedTouches[0];
        document.querySelectorAll("[data-droptarget]").forEach(t => { t.style.outline = ""; t.style.backgroundColor = ""; });
        const el = touchDragElRef.current;
        if (el) el.style.display = "none";
        const tgt = document.elementFromPoint(touch.clientX, touch.clientY);
        if (el) { el.remove(); touchDragElRef.current = null; }
        // Use rects stored at drag start time
        let drop = tgt ? tgt.closest("[data-droptarget]") : null;
        if (!drop && drag && drag.rects) {
          const x = touch.clientX;
          const y = touch.clientY;
          let foundTarget = null;
          // Add generous padding to account for card floating above finger
          const padding = 60;
          Object.entries(drag.rects).forEach(([key, rect]) => {
            if (x >= rect.left - padding && x <= rect.right + padding && 
                y >= rect.top - padding && y <= rect.bottom + padding) {
              foundTarget = key;
            }
          });
          if (foundTarget) {
            drop = { dataset: { droptarget: foundTarget } };
          }
        }
        const drag = touchDragRef.current;
        touchDragRef.current = null;

        if (!drop || !drag) return;
        if (drop.dataset.droptarget === "foundation") handleDropOnFoundationRef.current(drag);
        else if (drop.dataset.droptarget.startsWith("tableau-")) handleDropOnTableauRef.current(parseInt(drop.dataset.droptarget.split("-")[1]), drag);
      }}
    >

      {/* TOOLBAR */}
      <div style={{ height: TOOLBAR_H + "px", background: "rgba(0,0,0,0.5)", borderBottom: "1px solid rgba(201,168,76,0.3)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 10px", flexShrink: 0 }}>

        {/* Left: Back */}
        <button onClick={onBack} style={{ background: "transparent", color: "#c9a84c", border: "1px solid rgba(201,168,76,0.4)", padding: "8px 12px", fontSize: "0.7rem", fontWeight: "700", letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer", borderRadius: "2px", whiteSpace: "nowrap" }}>← Back</button>

        {/* Center: Title (desktop only) */}
        {!isMobile && <div style={{ fontFamily: "Playfair Display, serif", color: "#c9a84c", fontSize: "1rem", fontWeight: "700", letterSpacing: "0.2em", textTransform: "uppercase" }}>♠ Pounce League</div>}

        {/* Right: Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button onClick={handleRecycle} style={{ background: canRecycle ? "rgba(201,168,76,0.2)" : "transparent", color: canRecycle ? "#c9a84c" : "rgba(201,168,76,0.3)", border: `1px solid ${canRecycle ? "#c9a84c" : "rgba(201,168,76,0.2)"}`, padding: "8px 10px", fontSize: "0.7rem", fontWeight: "700", cursor: canRecycle ? "pointer" : "default", borderRadius: "2px", whiteSpace: "nowrap", transition: "all 0.2s" }}>{isMobile ? "♻️" : "♻️ Recycle"}</button>
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ color: "rgba(201,168,76,0.5)", fontSize: "0.6rem" }}>🔍</span>
              <input type="range" min="0.6" max="1.6" step="0.1" value={cardSize} onChange={e => setCardSize(parseFloat(e.target.value))} style={{ width: "80px", accentColor: "#c9a84c", cursor: "pointer" }} />
            </div>
          )}
          <button onClick={() => setSoundEnabled(!soundEnabled)} style={{ background: "transparent", color: soundEnabled ? "#c9a84c" : "rgba(201,168,76,0.3)", border: "none", fontSize: "1.2rem", cursor: "pointer", padding: "4px" }}>{soundEnabled ? "🔊" : "🔇"}</button>
          <button onClick={startNewGame} style={{ background: "linear-gradient(135deg, #c9a84c, #f0c040)", color: "#0d1b0f", border: "none", padding: "8px 12px", fontSize: "0.7rem", fontWeight: "700", letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer", borderRadius: "2px", whiteSpace: "nowrap" }}>{isMobile ? "New" : "New Game"}</button>
        </div>
      </div>

      {/* Difficulty */}
      <div style={{ textAlign: "center", padding: "3px", color: diffInfo.color, fontSize: "0.6rem", letterSpacing: "0.15em", textTransform: "uppercase", flexShrink: 0 }}>
        VS {diffInfo.name} — {diffInfo.description}
      </div>

      {/* AI AREA */}
      <div style={{ flex: "0 0 auto", padding: "6px 12px", background: "rgba(0,0,0,0.25)", borderBottom: "1px solid rgba(248,113,113,0.15)", overflowX: "auto" }}>
        {isMobile && <AIProgressBar name={gameState.ai.name} remaining={gameState.ai.pouncePile.length} total={13} />}
        {!isMobile && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
          <span style={{ color: "rgba(248,113,113,0.7)", fontSize: "0.7rem", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase" }}>🤖 {gameState.ai.name}</span>
          <span style={{ color: "rgba(201,168,76,0.6)", fontSize: "0.65rem" }}>Pounce: {gameState.ai.pouncePile.length}</span>
        </div>}
        {!isMobile && <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
          <div style={{ position: "relative", width: Math.round(cardW * 0.75) + "px", height: Math.round(cardH * 0.75) + "px", flexShrink: 0 }}>
            {gameState.ai.pouncePile.slice(-3).map((card, i) => (
              <div key={card.id} style={{ position: "absolute", top: i * 2 + "px", left: i + "px", zIndex: i }}>
                <Card card={card} cardSize={cs * 0.75} disabled />
              </div>
            ))}
          </div>
          <div style={{ width: "1px", height: Math.round(cardH * 0.75) + "px", background: "rgba(201,168,76,0.15)", flexShrink: 0 }} />
          {gameState.ai.tableau.map((stack, i) => (
            <div key={i}>{renderTableauStack(stack, i, true)}</div>
          ))}
          <div style={{ width: "1px", height: Math.round(cardH * 0.75) + "px", background: "rgba(201,168,76,0.15)", flexShrink: 0 }} />
          <Card card={gameState.ai.drawPile.length > 0 ? { ...gameState.ai.drawPile[0], faceUp: false } : null} cardSize={cs * 0.75} disabled />
          <Card card={gameState.ai.drawDiscard.length > 0 ? gameState.ai.drawDiscard[gameState.ai.drawDiscard.length - 1] : null} cardSize={cs * 0.75} disabled />
        </div>}
      </div>

      {/* FOUNDATIONS */}
      <div data-droptarget="foundation" onClick={handleDropOnFoundation} style={{ flex: "0 0 auto", padding: "6px 12px", borderBottom: "1px solid rgba(201,168,76,0.1)", overflowX: "auto" }}>
        <div style={{ textAlign: "center", color: aiFlash ? "#f87171" : "rgba(201,168,76,0.4)", fontSize: "0.6rem", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "4px", transition: "color 0.3s ease", fontWeight: aiFlash ? "700" : "400" }}>{aiFlash ? "🤖 DEALER PLAYED!" : "♦ Foundations — Ace to King ♦"}</div>
        <div data-droptarget="foundation" onClick={handleDropOnFoundation} style={{ display: "flex", gap: isMobile ? "2px" : "6px", flexWrap: "wrap", justifyContent: "center", padding: "4px", borderRadius: "8px" }}>
          {gameState.foundations.filter(pile => pile.length < 13).map((pile, i) => (
            <DropZone key={i} card={pile[pile.length - 1] || null} onDrop={handleDropOnFoundation} cardSize={isMobile ? cs * 0.85 : cs} />
          ))}
          {gameState.foundations.length < 8 && <DropZone card={null} onDrop={handleDropOnFoundation} label="A" cardSize={isMobile ? cs * 0.85 : cs} />}
        </div>
      </div>

      {/* MESSAGE */}
      <div style={{ textAlign: "center", padding: "3px", color: "#c9a84c", fontSize: "0.75rem", letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0, minHeight: "22px" }}>{message}</div>

      {/* PLAYER AREA */}
      <div style={{ flex: "0 0 auto", padding: "6px 12px", background: "rgba(0,0,0,0.2)", borderTop: "1px solid rgba(74,222,128,0.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
          <span style={{ color: "rgba(74,222,128,0.7)", fontSize: "0.7rem", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase" }}>👤 {gameState.player.name}</span>
          <span style={{ color: "rgba(201,168,76,0.6)", fontSize: "0.65rem" }}>Pounce: {gameState.player.pouncePile.length}</span>
        </div>

        {/* ROW 1: Tableau */}
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "8px", overflowX: "auto", justifyContent: isMobile ? "center" : "flex-start" }}>
          {gameState.player.tableau.map((stack, i) => (
            <div key={i} data-droptarget={"tableau-" + i} onClick={() => { if (selectedCard) handleDropOnTableau(i); }} onDragOver={e => e.preventDefault()} onDrop={() => handleDropOnTableau(i)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", flexShrink: 0, padding: "4px", borderRadius: "6px" }}>
              <span style={{ color: "rgba(201,168,76,0.4)", fontSize: "0.55rem", textTransform: "uppercase" }}>T{i + 1}</span>
              <div style={{ minHeight: cardH + "px" }}>
                {renderTableauStack(stack, i, false)}
              </div>
            </div>
          ))}
        </div>

        {/* ROW 2: Pounce pile + Draw + Discard */}
        <div style={{ display: "flex", gap: isMobile ? "20px" : "8px", alignItems: "flex-start", borderTop: "1px solid rgba(201,168,76,0.1)", paddingTop: "8px", justifyContent: isMobile ? "center" : "flex-start", marginTop: "8px" }}>

          {/* Pounce pile */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", flexShrink: 0 }}>
            <span style={{ color: "#c9a84c", fontSize: isMobile ? "0.75rem" : "0.55rem", textTransform: "uppercase", fontWeight: "700", letterSpacing: "0.05em" }}>Pounce ({gameState.player.pouncePile.length})</span>
            <div style={{ position: "relative", width: cardW + "px", height: cardH + "px" }}>
              {gameState.player.pouncePile.slice(-3).map((card, i, arr) => (
                <div key={card.id} style={{ position: "absolute", top: i * 2 + "px", left: i + "px", zIndex: i }}>
                  <Card
                    card={card}
                    cardSize={cs}
                    selected={selectedSource === "pouncePile" && i === arr.length - 1}
                    shake={shakingCard === card.id}
                    draggable={i === arr.length - 1 && card.faceUp}
                    onClick={() => handleCardClick("pouncePile", 0, false)}
                    onDragStart={() => handleDragStart(card, "pouncePile", 0, false)}
                      onTouchStart={(e) => startTouchDrag(e, card, "pouncePile", 0, false)}
                    onDragEnd={handleDragEnd}
                  />
                </div>
              ))}
              {gameState.player.pouncePile.length === 0 && <Card card={null} cardSize={cs} />}
            </div>
          </div>

          <div style={{ width: "1px", alignSelf: "stretch", background: "rgba(201,168,76,0.15)", flexShrink: 0 }} />

          {/* Draw pile */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", flexShrink: 0 }}>
            <span style={{ color: "#c9a84c", fontSize: isMobile ? "0.75rem" : "0.55rem", textTransform: "uppercase", fontWeight: "700", letterSpacing: "0.05em" }}>Draw ({gameState.player.drawPile.length})</span>
            <Card
              card={gameState.player.drawPile.length > 0 ? { ...gameState.player.drawPile[gameState.player.drawPile.length - 1], faceUp: false } : null}
              cardSize={cs}
              onClick={() => handleCardClick("draw", 0, false)}
            />
          </div>

          {/* Discard pile */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", flexShrink: 0 }}>
            <span style={{ color: "#c9a84c", fontSize: isMobile ? "0.75rem" : "0.55rem", textTransform: "uppercase", fontWeight: "700", letterSpacing: "0.05em" }}>Discard ({gameState.player.drawDiscard.length})</span>
            {gameState.player.drawDiscard.length > 0 ? (
              <Card
                card={gameState.player.drawDiscard[gameState.player.drawDiscard.length - 1]}
                cardSize={cs}
                selected={selectedSource === "drawDiscard"}
                shake={shakingCard === (gameState.player.drawDiscard[gameState.player.drawDiscard.length - 1]?.id)}
                draggable
                onClick={() => handleCardClick("drawDiscard", 0, false)}
                onDragStart={() => { const c = gameState.player.drawDiscard[gameState.player.drawDiscard.length - 1]; handleDragStart(c, "drawDiscard", 0, false); }}
              onTouchStart={(e) => { const c = gameState.player.drawDiscard[gameState.player.drawDiscard.length - 1]; startTouchDrag(e, c, "drawDiscard", 0, false); }}
                onDragEnd={handleDragEnd}
              />
            ) : (
              <Card card={null} cardSize={cs} />
            )}
          </div>
        </div>
      </div>

      {/* ROUND OVER */}
      {gameState.phase === "roundOver" && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.88)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 100, gap: "1rem" }}>
          <div style={{ fontSize: "3rem" }}>{gameState.player.score > gameState.ai.score ? "🎉" : "😔"}</div>
          <h2 style={{ fontFamily: "Playfair Display, serif", color: "#c9a84c", fontSize: "2rem", letterSpacing: "0.2em", textTransform: "uppercase", margin: 0 }}>
            {gameState.player.score > gameState.ai.score ? "You Win!" : "The Dealer Wins"}
          </h2>
          <div style={{ color: "#f5e6c8", fontSize: "1rem", textAlign: "center", lineHeight: 2 }}>
            <div>Your Score: <span style={{ color: "#4ade80" }}>{gameState.player.score}</span></div>
            <div>Dealer Score: <span style={{ color: "#f87171" }}>{gameState.ai.score}</span></div>
          </div>
          <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
            <button style={{ background: "linear-gradient(135deg, #c9a84c, #f0c040)", color: "#0d1b0f", border: "none", padding: "1rem 2rem", fontSize: "0.9rem", fontWeight: "700", fontFamily: "Playfair Display, serif", letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer", borderRadius: "2px" }} onClick={startNewGame}>Play Again</button>
            <button style={{ background: "transparent", color: "#c9a84c", border: "1px solid #c9a84c", padding: "1rem 2rem", fontSize: "0.9rem", fontWeight: "700", fontFamily: "Playfair Display, serif", letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer", borderRadius: "2px" }} onClick={onBack}>Main Menu</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}
